<!-- eslint-disable vue/require-component-is -->
<template>
  <div class="modal-value-help">
    <TableHeader
      ref="header-ref"
      :attrs="attrs"
      :multiple="multiple"
      :isBusy="isBusy"
      :isEditing="isEditing"
      :totalSelected="selectedValuesLength - (attrs.hiddenValues ?? 0)"
      :search.sync="search"
      :filterButton.sync="filterButton"
      @addNewData="handleAdd"
      @unselectAll="handleUnselectAll"
      @filter-change="handleFilterChange"
      @update:header-filter-full-object="(payload) => $emit('update:header-filter-full-object', payload)"
    />
    <div ref="table-container-ref" class="__table-container">
      <FormulateForm ref="formRef" v-model="formValues" @submit="handleSubmit">
        <b-table
          ref="table-ref"
          :key="refreshKey"
          show-empty
          :selectable="selectable"
          :select-mode="multiple ? 'multi' : 'single'"
          :no-select-on-click="isEditingRow !== null"
          :fields="computedFields"
          :items="items"
          :busy="isBusy"
          :tbody-tr-class="rowClass"
          class="-w-auto text-nowrap sticky-head-table border"
          :caption="showCaption ? $t('global.table.showingDataCount', { count: totalCount ?? 0 }) : null"
          @row-clicked="handleRowClicked"
        >
          <!-- Slot: Empty -->
          <template #empty>
            <div v-show="!isBusy" class="text-center my-4">{{ $t('global.emptyTable') }}</div>
          </template>

          <template v-if="attrs.trackBy === 'id' && canSelectAll" #head(checkbox)>
            <div class="form-check">
              <input
                v-model="selectAll"
                class="form-check-input me-1"
                type="checkbox"
                value="all"
                @input="toggleAll"
              />
            </div>
          </template>

          <!-- Slot: Cell Checkbox -->
          <template #cell(checkbox)="{ index, item }">
            <div class="form-check">
              <b-form-checkbox
                v-model="selectedValues[item[attrs.trackBy]]"
                :disabled="attrs.disableRowWhen && attrs.disableRowWhen(item)"
                @change="handleCheckboxChange($event, item, index)"
              />
            </div>
          </template>

          <!-- Table Header -->
          <template #head()="data">
            <div
              class="d-flex justify-content-between align-items-center"
              @click="handleSortChange(data)"
            >
              {{ data.label }}
              <template v-if="data.field.help">
                <i
                  :id="`tooltip-data-${data.field.key}`"
                  class="mc-fa"
                  :class="{
                    'icon-outline_info': hoveredKey !== data.field.key,
                    'icon-fill_info': hoveredKey === data.field.key,
                  }"
                  @mouseenter="hoveredKey = data.field.key"
                  @mouseleave="hoveredKey = null"
                />
                <component
                  :is="'b-popover'"
                  custom-class="popover-text-wrapper"
                  triggers="hover"
                  placement="topright"
                  :target="`tooltip-data-${data.field.key}`"
                >
                  {{ data.field.tooltip }}
                </component>
              </template>
              <template v-if="data.field.isSortable">
                <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                  <i class="mc-icon icon-outline_chevron-up"></i>
                </div>
                <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                  <i class="mc-icon icon-outline_chevron-down"></i>
                </div>
              </template>
            </div>
          </template>

          <!-- Table Data -->
          <template #cell()="{ index, field, value, item }">
            <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
              <FormulateInput
                v-bind="field"
                :ref="`${field.key}-ref`"
                :rowItem="item"
                :label="null"
                :name="field.inputName || field.key"
                :type="field.type || 'text'"
                :value="getInputInitialValue({ field, item })"
                :disabled="isSubmitting"
                :help="field?.validation?.includes('required')"
                size="sm"
                validation-behavior="live"
                error-behavior="live"
              >
                <template v-if="field.prefix" #prefix>
                  <div class="formulate-input-prefix">
                    <span>{{ field.prefix }}</span>
                  </div>
                </template>
              </FormulateInput>
            </template>

            <template v-else-if="field.isStatus">
              <span :class="field.formatter(get(item, field.key)).class">
                {{ field.formatter(get(item, field.key)).text }}
              </span>
            </template>

            <template v-else>
              <slot v-if="field.isSlot" :name="field.name" :item="item" :value="value">
                {{ value }}
              </slot>
              <template v-else-if="field.type === 'datetime'">
                <div>{{ formatIndexDate(value).date }}</div>
                <div class="mc-datetime">
                  {{ formatIndexDate(value).time }}
                </div>
              </template>
              <template v-else-if="field.type === 'redirectable'">
                <a
                  v-if="field?.isClickable?.(item[field?.key]) ?? true"
                  class="text-primary"
                  target="_blank"
                  @click.prevent="openInNewTab(item, field)"
                >
                  {{ item[field.key]?.[field.redirectLabel] }}
                </a>
                <span v-else>{{ field?.formatter?.(item[field?.key]) }}</span>
              </template>
              <template v-else>
                {{ formatValue(value, field) }}
              </template>
            </template>
          </template>

          <!-- Slot: Action Column (Last Column) -->
          <template #cell(action)="data">
            <div v-if="isEditingRow == data.index">
              <b-button
                variant="outline"
                :class="{
                  '__accept-button btn': true,
                  __disabled: hasErrors,
                }"
                type="submit"
              >
                <i class="mc-icon icon-fill_checkmark"></i>
              </b-button>

              <b-button
                variant="outline"
                class="__cancel-button btn"
                @click="handleEditCancel(data)"
              >
                <i class="mc-icon icon-outline_close"></i>
              </b-button>
            </div>

            <b-button
              v-else-if="attrs?.hasUpdateAction"
              variant="outline"
              :class="{
                '__edit-button btn': true,
                __disabled:
                  isEditingRow !== null ||
                  isBusy ||
                  (attrs.disableEditWhen && attrs.disableEditWhen(data)),
              }"
              @click="handleEditStart(data)"
            >
              <i class="mc-icon icon-outline_edit"></i>
            </b-button>
          </template>
        </b-table>
      </FormulateForm>

      <!-- Observe visibility to get next page data -->
      <div v-observe-visibility="handleElementObserved" style="height: 1px"></div>

      <!-- Skeleton when getting the data -->
      <div v-if="isBusy && hasNextPage" class="px-2 py-2 d-flex gap-2">
        <b-skeleton width="2.5%" />
        <b-skeleton width="97.5%" />
      </div>
    </div>

    <!-- TODO: Enhance custom create modal in next feature development -->
    <component :is="attrs.customCreateModal" v-if="attrs['has:createModal']" v-bind="attrs" />
  </div>
</template>

<script>
import TableHeader from './Header'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { Helper } from '@/mixin/helper'
import { formatIndexDate } from '@/helpers/tzDate'

export default {
  name: 'ModalTable',
  components: {
    TableHeader,
  },
  mixins: [Helper],
  props: {
    isBusy: {
      type: Boolean,
      default: false,
    },

    isEditing: {
      type: Boolean,
      default: false,
    },

    selectedModel: {},

    fullObject: {},

    totalCount: {},
    showCaption: {
      type: Boolean,
      default: false,
    },

    localItems: {
      type: Array,
      default: null,
    },

    attrs: {},

    listGetter: {},

    multiple: {
      type: Boolean,
      default: false,
    },

    selectable: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:isBusy', 'update:totalCount', 'update:isEditing'],
  data() {
    return {
      items: [],
      ids: [],
      sorts: [],
      page: 1,
      limit: 10,
      search: '',
      filterButton: this.attrs?.filterOptions?.default,
      filters: [],

      selectedValues: {},
      selectedObject: {},

      formValues: {},
      isEditingRow: null,
      isAdding: false,
      isSubmitting: false,
      isPagination: true,
      isResetting: false,
      selectAll: false,
      hoveredKey: null,

      editedData: {},
      refreshKey: 1,
      requestId: 0,
    }
  },
  computed: {
    isRowSelectable() {
      return (item) => {
        if (!this.selectable) return false
        if (this.attrs.disableRowWhen && this.attrs.disableRowWhen(item)) return false
        return true
      }
    },
    canSelectAll() {
      if (!this.attrs.canSelectAll) return false
      return this.attrs.canSelectAll
    },
    computedFields() {
      if (!this.attrs.tableFields) return []

      const fields = [...this.attrs.tableFields]
      if (this.multiple) {
        fields.unshift({
          key: 'checkbox',
          label: '',
          thStyle: 'width:20px;',
        })
      }

      if (this.isEditingRow !== null || this.attrs?.hasUpdateAction) {
        fields.push({
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        })
      }

      return fields
    },
    hasNextPage() {
      if (!this.isPagination) return false

      // If totalCount is null or undefined The data has not been fetched yet
      // so we return true to get the actual total count
      if (!this.totalCount) return true
      return this.page * this.limit < this.totalCount
    },
    selectedValuesLength() {
      return Object.keys(this.selectedValues).filter((value) => this.selectedValues[value]).length
    },
    hasErrors() {
      return this.$refs.formRef.hasErrors
    },
  },
  watch: {
    async search(newValue) {
      if (this.isResetting) return

      const opts = {
        limit: this.limit,
        search: newValue != '' ? newValue : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.sortKey || sort.key} ${sort.order}`),
        filterButton: this.filterButton,
        filters: this.filters,
      }
      await this.getData(opts, true)
      this.$refs['header-ref'].focusSearch()
    },
    async filterButton(newValue) {
      const opts = {
        limit: this.limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.sortKey || sort.key} ${sort.order}`),
        filterButton: newValue,
        filters: this.filters,
      }
      await this.getData(opts, true)
    },
    localItems: {
      immediate: true,
      handler(newValue) {
        if (Array.isArray(newValue)) {
          this.items = newValue
        }
      },
    },
  },
  async mounted() {
    this.$root.$on('refresh-value-help', this.handleRefreshValueHelp)

    // If there is no selected data from parent
    if (this.multiple && isEmpty(this.selectedModel)) return
    if (this.selectedModel == '' || this.selectedModel == null) return

    const selected = this.multiple ? this.selectedModel : [this.selectedModel]

    // set keys in selectedValues
    selected.forEach((value) => {
      this.$set(this.selectedValues, value, true)
    })

    if (!isEmpty(this.fullObject) && this.fullObject != undefined) {
      const fullObject = this.multiple ? this.fullObject : [this.fullObject]

      // set fullObject in selectedObject
      fullObject.forEach((obj) => {
        this.$set(this.selectedObject, obj[this.attrs.trackBy], obj)
      })
    }

    await this.$nextTick()
    this.syncSelectAllState()
  },
  beforeDestroy() {
    this.$root.$off('refresh-value-help', this.handleRefreshValueHelp)
    this.handleHide()
  },
  methods: {
    formatIndexDate,
    get,
    getSelectableIds() {
      if (!this.canSelectAll || this.ids.length === 0) return []

      return this.ids.filter((id) => {
        if (!this.attrs.disableRowWhen) return true

        let item = this.items.find((iterator) => iterator[this.attrs.trackBy] == id)
        if (item == undefined) {
          item = {
            [this.attrs.trackBy]: id,
            id,
          }
        }

        return !this.attrs.disableRowWhen(item)
      })
    },
    syncSelectAllState() {
      if (!this.canSelectAll) {
        this.selectAll = false
        return
      }

      const selectableIds = this.getSelectableIds()
      if (selectableIds.length === 0) {
        this.selectAll = false
        return
      }

      this.selectAll = selectableIds.every((id) => this.selectedValues[id])
    },
    handleRefreshValueHelp(name) {
      if (this.attrs.name === name) {
        this.isEditingRow = null
        this.isAdding = false
        this.refreshKey += 1
        this.$emit('update:isEditing', false)
        this.getData(
          {
            limit: this.limit,
            page: 1,
            sorts: this.sorts.map((sort) => `${sort.sortKey || sort.key} ${sort.order}`),
            filterButton: this.filterButton,
            filters: this.filters,
          },
          true
        )
      }
    },
    toggleAll(event) {
      let checked = event.target.checked
      if (checked) {
        this.handleSelectAll()
      } else {
        this.handleUnselectAll()
      }
    },
    handleAdd() {
      this.$emit('addNewData')

      if (this.attrs.hasAddAction) return

      if (this.attrs['has:createModal']) {
        this.$bvModal.show(`${this.attrs.name}-create-data-modal`)
        return
      }

      this.$emit('update:isEditing', true)

      if (this.isEditingRow != null) return
      this.isEditingRow = 0
      this.isAdding = true
      this.items = [{ id: 0 }, ...this.items]
    },
    HandleApply() {
      return {
        selectedValues: this.selectedValues,
        selectedObject: this.selectedObject,
      }
    },
    rowClass(item) {
      if (!item) return
      if (this.attrs.disableRowWhen && this.attrs.disableRowWhen(item)) {
        return 'mc-table-row--disabled'
      }
    },
    handleHide() {
      this.requestId += 1
      this.isResetting = true
      this.$emit('update:isBusy', false)
      this.items = []
      this.ids = []
      this.sorts = []
      this.page = 1
      this.search = ''

      this.isEditingRow = null
      this.isSubmitting = false
      this.$nextTick(() => {
        this.isResetting = false
      })
    },
    async handleFilterChange(filters) {
      this.filters = filters
      const opts = {
        limit: this.limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.sortKey || sort.key} ${sort.order}`),
        filterButton: this.filterButton,
        filters: this.filters,
      }
      await this.getData(opts, true)
    },
    async getData(opts, reset = false) {
      const requestId = ++this.requestId
      this.$emit('update:isBusy', true)

      if (!this.listGetter) {
        this.$emit('update:isBusy', false)
        return
      }

      let res
      try {
        res = await this.listGetter(opts)
      } catch {
        if (requestId === this.requestId) {
          this.$emit('update:isBusy', false)
        }
        return
      }

      if (requestId !== this.requestId) return
      if (!res || res.errors) {
        this.$emit('update:isBusy', false)
        return
      }

      const trackBy = this.attrs.trackBy

      const newItems = res.data?.paginated_result || []
      if (reset || !trackBy) {
        this.items = newItems
      } else {
        const existingItemKeys = new Set(this.items.map((item) => item[trackBy]))
        const uniqueNewItems = []
        for (const item of newItems) {
          const key = item[trackBy]
          if (!existingItemKeys.has(key)) {
            existingItemKeys.add(key)
            uniqueNewItems.push(item)
          }
        }
        this.items = [...this.items, ...uniqueNewItems]
      }

      const newIds = res.data?.ids || []
      if (reset || !trackBy) {
        this.ids = newIds
      } else {
        const existingIds = new Set(this.ids)
        const uniqueNewIds = []
        for (const id of newIds) {
          if (!existingIds.has(id)) {
            existingIds.add(id)
            uniqueNewIds.push(id)
          }
        }
        this.ids = [...this.ids, ...uniqueNewIds]
      }
      
      this.page = res.metadata.page
      this.isPagination = res.isPagination !== undefined ? res.isPagination : true

      this.$emit('update:totalCount', res.metadata.total_count)
      this.$emit('update:isBusy', false)

      await this.$nextTick()
      if (requestId !== this.requestId) return

      if (Object.keys(this.selectedValues).length > 0) {
        this.highlightSelectedRow()
      }

      if (!this.multiple && this.selectedModel) {
        const index = this.items.findIndex((item) => item[this.attrs.trackBy] == this.selectedModel)
        if (index !== -1) {
          this.$refs['table-ref'].selectRow(index)
        }
      }

      if (res.data.paginated_result.length === 0 && !this.search) {
        this.page = 1
        this.$emit('available-change', false)
        if (this.hasActiveFilters) {
          this.$emit('available-change', true)
        }
      }

      this.syncSelectAllState()
      this.$emit('row-selected', this.selectedValuesLength)
    },
    // highlight the row if there is selected value from parent
    highlightSelectedRow() {
      const trackBy = this.attrs.trackBy

      // Get all selected keys from selectedValues
      const selectedKeys = Object.keys(this.selectedValues).filter(
        (key) => this.selectedValues[key]
      )

      selectedKeys.forEach((value) => {
        // intentionally used == instead of === because selectedModel can be a string or number (id or uuid)
        const index = this.items.findIndex((item) => item[trackBy] == value)

        // Toggle the row checkbox
        if (index !== -1) {
          this.$refs['table-ref'].selectRow(index)
        }
      })
    },
    handleElementObserved(isVisible) {
      if (!isVisible || this.isBusy) return
      if (!this.hasNextPage) return
      // if there is no items and has the search query
      // then we don't have to get data by observer
      if (this.items.length == 0 && this.search) return

      // if total count is null, it means that we haven't fetch the data (initial)
      // do not increment page, but still fetch the data
      this.getData({
        limit: this.limit,
        page: this.totalCount ? this.page + 1 : this.page,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.sortKey || sort.key} ${sort.order}`),
        filterButton: this.filterButton,
        filters: this.filters,
      })
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    async handleSortChange(data) {
      // Sorting function is disabled when editing table
      if (this.isEditing) return

      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            sortKey: currentColumnSort.sortKey,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({
            key: data.field.key,
            sortKey: data.field.sortKey,
            order: 'asc',
          })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData(
        {
          limit: this.limit,
          page: 1,
          search: this.search != '' ? this.search : undefined,
          sorts: this.sorts.map((sort) => `${sort.sortKey || sort.key} ${sort.order}`),
          filterButton: this.filterButton,
          filters: this.filters,
        },
        true
      )
    },
    // assign selected object to state
    assignSelectedObject(checked, item) {
      if (!this.multiple) {
        this.selectedObject = {}
      }

      if (checked) {
        this.$delete(this.selectedObject, item[this.attrs.trackBy])
        return
      }

      this.$set(this.selectedObject, item[this.attrs.trackBy], item)
    },
    handleRowClicked(item, index) {
      if ((this.attrs.hasStoreAction || this.attrs.hasUpdateAction) && this.isEditingRow !== null)
        return

      if (this.attrs.disableRowWhen && this.attrs.disableRowWhen(item)) return

      const trackBy = this.attrs.trackBy

      // Last checked value before the user clicked on the row
      const checked = this.selectedValues[item[trackBy]]

      // To protect when chexbox state (selectedValues)
      // is not synced with b-table select state
      // Force select/unselect to match the checkbox state
      const toggleRowSelection = !checked ? 'unselectRow' : 'selectRow'
      this.$refs['table-ref'][toggleRowSelection](index)

      // assign the fullObject to be emit when applied
      this.assignSelectedObject(checked, item)

      if (this.multiple) {
        this.$set(this.selectedValues, item[trackBy], !checked)
      } else {
        this.selectedValues = {
          [item[trackBy]]: !checked,
        }
      }

      this.syncSelectAllState()
      this.$emit('row-selected', this.selectedValuesLength)
    },
    // if the user clicks on the checkbox (not on the row)
    handleCheckboxChange(targetChecked, item, index) {
      if (this.attrs.disableRowWhen && this.attrs.disableRowWhen(item)) return

      this.assignSelectedObject(!targetChecked, item)

      if (targetChecked) {
        this.$refs['table-ref'].selectRow(index)
      } else {
        this.$refs['table-ref'].unselectRow(index)
      }

      this.syncSelectAllState()
    },
    handleUnselectAll() {
      this.selectedValues = {}
      this.selectedObject = {}
      this.$refs['table-ref'].clearSelected()
      this.syncSelectAllState()
    },
    // TODO: Discuss with backend how to handle
    // select all when trackBy is not id
    handleSelectAll() {
      if (this.attrs.trackBy === 'id') {
        // TODO: handle assign the full object in select all
        this.ids.forEach((id) => {
          let item = this.items.find((item) => item.id == id)

          if (item == undefined) {
            item = {
              id: id,
            }
          }

          if (this.attrs.disableRowWhen && this.attrs.disableRowWhen(item)) return

          this.$set(this.selectedValues, id, true)
          this.$set(this.selectedObject, id, item)
        })
        this.syncSelectAllState()
      } else {
        throw new Error('Select all is not yet implemented when trackBy is not id')
      }
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    async submitStore(data) {
      const createdData = await this.attrs.apiClient.store(data)
      if (createdData.errors) return createdData

      const refetchedData = await this.attrs.apiClient.show(createdData.data.id)
      if (refetchedData.errors) return refetchedData

      this.ids.push(refetchedData.data.id)
      this.$emit('update:totalCount', this.totalCount + 1)

      this.$mcToast.success(this.$t('global.alert.text.added'))

      return {
        ...data,
        ...refetchedData.data,
      }
    },
    async submitUpdate(data, oldData) {
      const updateData = await this.attrs.apiClient.update(
        this.items[this.isEditingRow].id,
        data,
        oldData
      )
      if (updateData.errors) return updateData

      const refetchedData = await this.attrs.apiClient.show(this.items[this.isEditingRow].id)
      if (refetchedData.errors) return refetchedData

      this.$mcToast.success(this.$t('global.alert.text.changed'))

      const updatedItem = {
        ...this.items[this.isEditingRow],
      }

      this.attrs.tableFields.forEach((field) => {
        if (field.isEditable) {
          updatedItem[field.key] = refetchedData[field.key]
        }
      })

      return updatedItem
    },
    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning(this.$t('global.alert.text.pleaseWait'))
        return
      }

      this.isSubmitting = true

      const transformedData = this.transformFields(data, this.computedFields)

      let newData = null

      if (this.isAdding) {
        newData = await this.submitStore(transformedData)
      } else {
        newData = await this.submitUpdate(this.formValues, this.editedData)
      }

      if (newData?.errors) {
        this.isSubmitting = false

        this.$mcToast.error(newData?.message ?? this.$t('global.somethingWentWrong'))
        return
      }

      this.$set(this.items, this.isEditingRow, newData)

      const idxSelectedRow = this.isEditingRow
      this.isEditingRow = null

      this.handleRowClicked(newData, idxSelectedRow)
      this.refreshKey += 1

      this.$nextTick(() => {
        this.$refs['table-ref'].selectRow(idxSelectedRow)
      })

      this.isAdding = false
      this.isSubmitting = false
      this.$emit('update:isEditing', false)
    },
    formatValue(value, field) {
      if (field.formatValue === undefined) {
        return value
      }

      return field.formatValue(value)
    },

    async handleEditStart(data) {
      if (
        this.isEditingRow !== null ||
        this.isBusy ||
        (this.attrs.disableEditWhen && this.attrs.disableEditWhen(data))
      )
        return

      this.attrs.tableFields.map((field) => {
        if (field.isEditable) {
          this.$set(this.editedData, field.inputName, data.item[field.key])
        }
      })
      this.$emit('update:isEditing', true)
      this.isEditingRow = data.index
      await this.$nextTick()
      this.$refs[`${this.attrs.tableFields[0].key}-ref`]?.$el.querySelector('input').focus()
    },

    async handleEditCancel() {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      }

      this.isEditingRow = null
      this.refreshKey += 1

      this.$nextTick(() => {
        this.highlightSelectedRow()
      })
      this.$emit('update:isEditing', false)
    },

    getInputInitialValue({ item, field }) {
      // get value from callback in field setting
      // can be used for formatting select input
      if (field.customValueFn) {
        return field.customValueFn(item) || false
      }

      // get value from item
      if (typeof item[field.key] !== 'undefined') {
        return item[field.key]
      }

      return false
    },
    openInNewTab(item, field) {
      const routeData = this.$router.resolve({
        name: field.redirectTo[item[field.key]?.type],
        params: { id: item[field.key]?.[field.redirectId] },
      })
      window.open(routeData.href, '_blank')
    },
  },
}
</script>

<style lang="scss" scoped>
.__table-container {
  border: 1px solid #ebecef;
  height: 300px;
  overflow-y: auto;

  :deep(.table) {
    --bs-table-active-bg: #e9f2fc;

    thead {
      tr {
        th {
          position: relative;
          &:has(.sort-icon-up),
          &:has(.sort-icon-down) {
            padding-right: 30px;
          }

          .sort-icon-up {
            position: absolute;
            right: 10px;
            top: 8px;
            opacity: 0.7;
          }
          .sort-icon-down {
            position: absolute;
            right: 10px;
            bottom: 8px;
            opacity: 0.7;
          }

          .mc-fa {
            font-size: 16px;
          }
        }
      }
    }

    // Action column
    .mc-action-col-head {
      width: 80px;
    }

    // Action cell
    .mc-action-cell {
      vertical-align: middle;

      & > div {
        display: flex;
        padding: 0 4px;
        justify-content: space-between;
      }

      .mc-icon:before {
        font-size: 20px;
      }

      .btn {
        padding: 0;
      }

      .btn:hover {
        border-color: transparent;
      }

      .__cancel-button .mc-icon:before {
        color: $danger;
      }

      .__disabled .mc-icon:before {
        opacity: $btn-disabled-opacity;
        cursor: not-allowed;
      }

      .__accept-button .mc-icon:before {
        color: $success;
      }
    }
  }

  ::v-deep .mc-table-row--disabled {
    background-color: #f8f9fa;
    color: #6c757d;
    cursor: not-allowed;
    pointer-events: none;

    td {
      background-color: #D6DBE0 !important;
      pointer-events: none;
    }

    .form-check-input {
      pointer-events: none;
      opacity: 0.5;
    }

    .mc-status {
      opacity: 0.6;
    }
  }
}
</style>
<style lang="scss">
.popover-text-wrapper .popover-body {
  font-family: $font-family-sans-serif;
  font-size: 12px;
  background-color: $mep-color-text-400 !important;
  color: $white !important;
  max-width: 200px;
  padding: 6px 12px !important;
}
</style>
